import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/contexts/AuthContext";
import { AUTH } from "@/constants/testIds";

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/app");
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1637430308606-86576d8fef3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBmaXRuZXNzJTIwc3R1ZGlvfGVufDB8fHx8MTc4NDQzMDE2NHww&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 h-full w-full object-cover"
          alt=""
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[10px] uppercase tracking-[0.28em] mb-3 opacity-80">Kairosa · Wellness OS</div>
          <div className="font-display text-4xl leading-tight max-w-md">
            One platform. Infinite wellness businesses.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 relative">
        <Link
          to="/"
          data-testid="back-to-site-login"
          className="absolute top-6 left-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
        <form onSubmit={submit} className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-10">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-lg leading-none">K</div>
            <div className="font-display text-xl">Kairosa</div>
          </Link>

          <h1 className="font-display text-4xl mb-2">Welcome back.</h1>
          <p className="text-muted-foreground mb-8 text-sm">
            Sign in to your wellness workspace.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid={AUTH.loginEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                data-testid={AUTH.loginPassword}
                testId="login-password-toggle"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                containerClassName="mt-1.5"
              />
            </div>
            {error && (
              <div data-testid={AUTH.loginError} className="text-sm text-destructive">{error}</div>
            )}
            <Button
              data-testid={AUTH.loginSubmit}
              type="submit"
              disabled={busy}
              className="w-full rounded-full h-11 mt-2"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>

          <div className="mt-8 text-center text-sm text-muted-foreground">
            No account?{" "}
            <Link to="/register" className="text-foreground underline underline-offset-4">
              Create your business
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
